import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X,
  Package,
  ShoppingCart,
  Loader2,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Download,
  Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { formatCurrency, formatDate } from '../lib/utils';
import { Order, ProcurementBatchItem } from '../types';
import { 
  getEligibleOrdersForProcurement, 
  calculateShoppingList, 
  createProcurementBatch,
  updateProcurementBatchStatus,
  fetchProcurementBatchById,
  generatePublicProcurementLink
} from '../services/procurementService';

interface CreateBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBatchCreated?: (batchId: string) => void;
}

export function CreateBatchModal({ 
  isOpen, 
  onClose, 
  onBatchCreated 
}: CreateBatchModalProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [shoppingList, setShoppingList] = useState<ProcurementBatchItem[]>([]);
  const [prioritizePriorityOrders, setPrioritizePriorityOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load eligible orders when modal opens
  useEffect(() => {
    const loadOrders = async () => {
      if (!isOpen) return;
      
      try {
        setLoading(true);
        setError(null);
        const eligibleOrders = await getEligibleOrdersForProcurement();
        setOrders(eligibleOrders);
        
        // Auto-select priority orders if prioritize toggle is on
        if (prioritizePriorityOrders) {
          const priorityOrderIds = eligibleOrders
            .filter(order => order.shipping?.priorityProcessing)
            .map(order => order.id);
          setSelectedOrderIds(new Set(priorityOrderIds));
        }
      } catch (error) {
        console.error('Failed to load eligible orders:', error);
        setError('Failed to load eligible orders');
        toast.error('Failed to load eligible orders');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [isOpen, prioritizePriorityOrders]);

  // Recalculate shopping list when selected orders change
  useEffect(() => {
    const updateShoppingList = async () => {
      if (selectedOrderIds.size === 0) {
        setShoppingList([]);
        return;
      }

      try {
        setCalculating(true);
        const selectedOrders = orders.filter(order => selectedOrderIds.has(order.id));
        const list = await calculateShoppingList(selectedOrders);
        setShoppingList(list);
      } catch (error) {
        console.error('Failed to calculate shopping list:', error);
        toast.error('Failed to calculate shopping list');
      } finally {
        setCalculating(false);
      }
    };

    updateShoppingList();
  }, [selectedOrderIds, orders]);

  const handleOrderSelect = (orderId: string, selected: boolean) => {
    const newSelection = new Set(selectedOrderIds);
    if (selected) {
      newSelection.add(orderId);
    } else {
      newSelection.delete(orderId);
    }
    setSelectedOrderIds(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedOrderIds.size === orders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map(order => order.id)));
    }
  };

  const handlePriorityToggle = (enabled: boolean) => {
    setPrioritizePriorityOrders(enabled);
    if (enabled) {
      // Auto-select priority orders
      const priorityOrderIds = orders
        .filter(order => order.shipping?.priorityProcessing)
        .map(order => order.id);
      setSelectedOrderIds(new Set(priorityOrderIds));
    } else {
      setSelectedOrderIds(new Set());
    }
  };

  const totalCost = shoppingList.reduce((sum, item) => sum + item.lineTotal, 0);
  const priorityOrdersCount = orders.filter(order => 
    order.shipping?.priorityProcessing && selectedOrderIds.has(order.id)
  ).length;

  const handleGeneratePDF = async () => {
    if (selectedOrderIds.size === 0) {
      toast.error('Please select at least one order');
      return;
    }

    try {
      setCreating(true);
      
      // Create the batch
      const batchId = await createProcurementBatch(
        Array.from(selectedOrderIds),
        shoppingList,
        priorityOrdersCount > 0,
        'current-user' // TODO: Replace with actual user ID
      );

      // Generate public link for the batch
      const publicUrl = await generatePublicProcurementLink(batchId);
      
      // Update batch status to generated
      await updateProcurementBatchStatus(batchId, 'generated');
      
      // Copy link to clipboard
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Procurement batch created and public link copied to clipboard!');
      onBatchCreated?.(batchId);
      onClose();
      
    } catch (error) {
      console.error('Failed to create batch:', error);
      toast.error('Failed to create procurement batch');
    } finally {
      setCreating(false);
    }
  };

  const isPriorityOrder = (order: Order) => order.shipping?.priorityProcessing;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <ShoppingCart className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Create Procurement Batch
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm mt-1"
                    >
                      Select orders and generate a purchase order PDF
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
              
                {/* Priority Orders Toggle */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-amber-600 to-orange-600 rounded-lg flex items-center justify-center">
                        <Zap className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Prioritize Priority Orders</h3>
                        <p className="text-sm text-gray-600">
                          Automatically select orders with priority processing
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={prioritizePriorityOrders}
                      onCheckedChange={handlePriorityToggle}
                    />
                  </div>
                </motion.div>

                {/* Orders Selection */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Eligible Orders ({orders.length})
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={loading}
                      className="hover:bg-gray-50"
                    >
                      {selectedOrderIds.size === orders.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                {error ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Orders</h3>
                    <p className="text-gray-600">{error}</p>
                  </div>
                ) : loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    <span className="ml-2 text-gray-600">Loading eligible orders...</span>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Eligible Orders</h3>
                    <p className="text-gray-600">
                      All paid, unpacked orders are already in procurement batches.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-12 font-semibold text-gray-700"></TableHead>
                          <TableHead className="font-semibold text-gray-700">Order</TableHead>
                          <TableHead className="font-semibold text-gray-700">Customer</TableHead>
                          <TableHead className="font-semibold text-gray-700">Date</TableHead>
                          <TableHead className="font-semibold text-gray-700">Items</TableHead>
                          <TableHead className="font-semibold text-gray-700">Total</TableHead>
                          <TableHead className="font-semibold text-gray-700">Priority</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order, index) => (
                          <motion.tr
                            key={order.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.02 }}
                            className={`hover:bg-gray-50 transition-all duration-300 hover:shadow-sm ${
                              isPriorityOrder(order) ? 'bg-amber-50/50 border-l-4 border-l-amber-400' : ''
                            }`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedOrderIds.has(order.id)}
                                onCheckedChange={(checked) => handleOrderSelect(order.id, checked as boolean)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-gray-900">
                                {order.orderNumber || order.id}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-900">{order.customer.name}</div>
                              <div className="text-sm text-gray-500">{order.customer.email}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">
                                {formatDate(order.createdAt)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{order.items.length}</span>
                            </TableCell>
                            <TableCell>
                              <div className="font-semibold text-gray-900">
                                {formatCurrency(order.total)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {isPriorityOrder(order) ? (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 hover:text-amber-800 text-[10px] px-1.5 py-0.5 h-5 leading-none">
                                  ⚡ Priority
                                </Badge>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                          </motion.tr>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </motion.div>

                {/* Shopping List */}
                {selectedOrderIds.size > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Aggregated Shopping List
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedOrderIds.size} orders selected • {shoppingList.length} unique items
                      </p>
                    </div>

                  {calculating ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                      <span className="ml-2 text-gray-600">Calculating shopping list...</span>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="font-semibold text-gray-700">Product</TableHead>
                              <TableHead className="font-semibold text-gray-700">Order Quantity</TableHead>
                              <TableHead className="font-semibold text-gray-700">Listing Quantity</TableHead>
                              <TableHead className="font-semibold text-gray-700">Required Quantity</TableHead>
                              <TableHead className="font-semibold text-gray-700">Unit Cost</TableHead>
                              <TableHead className="font-semibold text-gray-700">Line Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {shoppingList.map((item, index) => (
                              <motion.tr
                                key={item.productId}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.02 }}
                                className="hover:bg-gray-50 transition-all duration-300 hover:shadow-sm"
                              >
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <img 
                                      src={item.image} 
                                      alt={item.productName}
                                      className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium text-gray-900">
                                        {item.productName}
                                      </span>
                                      {item.brand && (
                                        <span className="text-xs text-gray-500 italic">
                                          {item.brand}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="font-medium text-gray-900">
                                    {item.orderQuantity}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="font-medium text-gray-900">
                                    {item.listingQuantity}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="font-semibold text-gray-900">
                                    {item.requiredQuantity}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="text-gray-600">
                                    {formatCurrency(item.unitCost)}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="font-semibold text-gray-900">
                                    {formatCurrency(item.lineTotal)}
                                  </span>
                                </TableCell>
                              </motion.tr>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Total */}
                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="text-lg font-semibold text-gray-900">
                            Batch Total:
                          </div>
                          <div className="text-2xl font-bold text-emerald-600 flex items-center gap-2">
                            <DollarSign className="h-6 w-6" />
                            {formatCurrency(totalCost)}
                          </div>
                        </div>
                        {priorityOrdersCount > 0 && (
                          <div className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                            <Zap className="h-4 w-4" />
                            {priorityOrdersCount} priority order{priorityOrdersCount !== 1 ? 's' : ''} included
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </div>

          {/* Enhanced Footer */}
          {selectedOrderIds.size > 0 && (
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ShoppingCart className="h-4 w-4" />
                  <span>
                    {selectedOrderIds.size} order{selectedOrderIds.size !== 1 ? 's' : ''} selected • 
                    {shoppingList.length} unique item{shoppingList.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={creating}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGeneratePDF}
                    disabled={creating || calculating || selectedOrderIds.size === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Batch...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Create Batch
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
      )}
    </AnimatePresence>
  );
}